import styles from './QuickMenu.scss';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { Motion, spring, presets } from 'react-motion';
import lodash from 'lodash';
import TetherComponent from 'react-tether';
import icons from 'styles/icons/icons.scss';
import { trackEvent } from 'components/common/GoogleAnalytics/GoogleAnalytics';

const menuItemHeight = 30;

export class QuickMenu extends Component {
    constructor(props) {
        super(props);
        this.state = {
            focused: false,
            isMenuOutOfView: false,
            menuOffsetClass: '',
        };
        this.onOutsideClick = this.onOutsideClick.bind(this);
    }

    componentDidMount() {
        if (document.addEventListener) {
            document.addEventListener('click', this.onOutsideClick, true);
        }
    }

    componentWillUnmount() {
        if (document.removeEventListener) {
            document.removeEventListener('click', this.onOutsideClick, true);
        }
    }

    onClearFocus() {
        const { focused } = this.state;
        if (!focused) {
            return;
        }

        this.setFocus(false);
    }

    setFocus(focused) {
        this.setState({ focused }, () => this.setState({ menuOffsetClass: '' }));
        this.trackEvent(focused ? 'Opened' : 'Closed');
    }

    onOutsideClick(e) {
        const bodyNode = findDOMNode(this.refs.menuBody);

        if (lodash.isNil(bodyNode)) {
            return;
        }

        const isDescendant = bodyNode.contains(e.target);

        if (!isDescendant) {
            this.onClearFocus();
        }
    }

    getOptionsStyle() {
        const { focused } = this.state;

        if (focused) {
            return {
                height: spring(menuItemHeight, presets.gentle),
                opacity: spring(1, presets.gentle),
            };
        }
        return {
            height: 0,
            opacity: 0,
        };
    }

    handleOptionClick({ handler, label }) {
        return (e) => {
            this.onClearFocus();
            this.trackEvent(label);
            handler(e);
        };
    }

    setIsMenuOutOfView(shouldFocus) {
        const menuElm = this.refs.menuHeader;

        if (menuElm) {
            // menuItemHeight (30) is the style height being passed in via the Motion component
            const menuItemsHeight = menuItemHeight * this.props.options.length;
            const { bottom: menuBottomHeight } = menuElm.getBoundingClientRect();
            const bodyHeight = document.body.clientHeight;
            const isMenuOutOfView = menuBottomHeight + menuItemsHeight > bodyHeight;

            return this.setState(
                {
                    isMenuOutOfView,
                    menuOffsetClass: isMenuOutOfView ? styles.menuOffset : '',
                },
                this.setFocus.bind(this, shouldFocus)
            );
        }

        this.setState({ isMenuOutOfView: false }, this.setFocus.bind(this, shouldFocus));
    }

    trackEvent(label) {
        const category = `Quick Menu: ${this.props.headerText}`;
        const action = this.extractActionTextFromLabel(label);
        trackEvent({ category, action });
    }

    // This hack is needed because there are several locations where label is
    // passed in as a react object. This is done in UserQuickActions and
    // ExternalUsers so the part 11 indicator can be displayed. This control has
    // been deprecated and therefore this should not be a problem anywhere else
    // moving forward.
    extractActionTextFromLabel(label) {
        return typeof label === 'object' ? lodash.get(label, 'props.labelText', '') : label;
    }

    render() {
        const { focused, isMenuOutOfView, menuOffsetClass } = this.state;
        const { headerText, options } = this.props;
        const theme = this.props.theme || 'primary';

        const qmTheme = styles[theme + 'Qm'];

        return (
            <TetherComponent
                attachment={isMenuOutOfView ? 'bottom right' : 'top right'}
                targetAttachment={isMenuOutOfView ? 'bottom right' : 'top right'}
                constraints={[{ to: 'scrollParent' }]}
                data-cy="QuickMenu"
            >
                {
                    <div ref="menuHeader" className={qmTheme} onClick={this.setIsMenuOutOfView.bind(this, !focused)}>
                        <span data-cy="QuickMenu">{headerText}</span>
                        <span className={icons.materialIcons}>expand_more</span>
                    </div>
                }
                {focused && (
                    <div ref="menuBody" className={`${qmTheme} ${menuOffsetClass}`}>
                        <div className={styles.body} data-cy="QuickMenu-body">
                            {isMenuOutOfView && (
                                <MenuOptions
                                    options={options}
                                    optionsStyle={this.getOptionsStyle()}
                                    handleOptionClick={this.handleOptionClick.bind(this)}
                                />
                            )}
                            <header
                                className={styles.header}
                                onClick={this.setFocus.bind(this, !focused)}
                                data-cy="QuickMenu-setFocus"
                            >
                                <span data-cy="QuickMenu-headerText">{headerText}</span>
                                <span className={icons.materialIcons}>expand_less</span>
                            </header>
                            {!isMenuOutOfView && (
                                <MenuOptions
                                    options={options}
                                    optionsStyle={this.getOptionsStyle()}
                                    handleOptionClick={this.handleOptionClick.bind(this)}
                                />
                            )}
                        </div>
                    </div>
                )}
            </TetherComponent>
        );
    }
}

const MenuOptions = ({ options, optionsStyle, handleOptionClick }) => {
    return (
        <section className={styles.options} data-cy="QuickMenu-MenuOptions">
            <Motion defaultStyle={{ height: 0, opacity: 0 }} style={optionsStyle}>
                {(style) => {
                    return (
                        <ul className={styles.optionList} data-cy="QuickMenu-ul">
                            {options.map((o, i) => {
                                return (
                                    <li
                                        key={i}
                                        data-cy={`QuickMenu-${i}`}
                                        style={style}
                                        onClick={handleOptionClick.call(this, o)}
                                    >
                                        <label data-cy="QuickMenu-label">{o.label}</label>
                                    </li>
                                );
                            })}
                        </ul>
                    );
                }}
            </Motion>
        </section>
    );
};

QuickMenu.propTypes = {
    headerText: PropTypes.string.isRequired,
    theme: PropTypes.oneOf(['primary', 'secondary', 'documentView', 'documentListItem']),
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
            handler: PropTypes.func.isRequired,
        })
    ).isRequired,
};

export default QuickMenu;
